/* AddModal.jsx */

import React, { useEffect, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './AddModal.css';

// Components
import Button from 'components/button/Button';
import CreateNewSection from 'components/addmodal/sections/createnew/CreateNewSection';
import CreateTypeSection from 'components/addmodal/sections/createtype/CreateTypeSection';
import DictionarySection from 'components/addmodal/sections/dictionary/DictionarySection';
import Divider from 'components/divider/Divider';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Tabs from 'components/tabs/Tabs';

// Utilities
import { ADD_MODAL_TABS } from 'components/addmodal/utilities/constants';

// Hooks
import { useFindExisting } from 'components/addmodal/sections/find/useFindExisting';
import { useCreateTypeForm } from 'components/addmodal/sections/createtype/useCreateTypeForm';
import { useDictionarySection } from 'components/addmodal/sections/dictionary/useDictionarySection';

/**
 * AddModal
 *
 * This component renders a modal for adding context to a node. The default view
 * allows selecting existing workspace types (or attaching existing nodes via search).
 * Users can open the "More" panel to search dictionary types or create a new type.
 *
 * @param {Object} node - The node to which context is being added.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Function to call when the modal is closed.
 * @param {function} onTypeSelect - Function to call when a type is selected.
 * @param {function} onExistingAttached - Function to call when an existing node is attached.
 * @param {string|null} anchorNodeKey - The key of the anchor node, if applicable.
 *
 * @returns {ReactNode} The rendered component.
 */
const AddModal = React.memo(({
    insertMode = false,
    node,
    isOpen,
    onClose,
    onExistingSelected,
    onTypeSelect,
    onExistingAttached,
    anchorNodeKey = null,
    showInjectedAddTypes = false,
}) => {

    // ----- CONTEXT ----- //

    // Theme Context
    const { theme } = useTheme();

    // Node Types Context
    const { getAll } = useNodeTypes();

    // User Context
    const { user } = useUser();

    // Workspace Context
    const { activeWorkspace } = useWorkspace();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [isMorePanelOpen, setIsMorePanelOpen] = useState(false);
    const [morePanelTab, setMorePanelTab] = useState(ADD_MODAL_TABS.DICTIONARY);

    // ----- DERIVED VALUES ----- //

    // Workspace Key
    const workspaceKey = activeWorkspace?.key || null;

    // User Key
    const userKey = user?.key || null;

    // Type Definitions
    const typeDefs = getAll() || [];

    // Sorted Types
    const availableTypes = [...typeDefs].sort((a, b) => a.label.localeCompare(b.label));

    // Map of available types for easy lookup by key in search results
    const availableTypeMap = new Map();
    availableTypes.forEach((typeDef) => {
        if (typeDef?.key) availableTypeMap.set(typeDef.key, typeDef);
    });

    // Active tab value passed to section hooks for gating data loading/search.
    const activePanelTab = isMorePanelOpen ? morePanelTab : ADD_MODAL_TABS.CREATE;

    //----- EFFECTS ----- //

    useEffect(() => {
        if (!isOpen) return;

        setIsMorePanelOpen(false);
        setMorePanelTab(ADD_MODAL_TABS.DICTIONARY);
    }, [isOpen]);

    //----- HOOKS ----- //

    // Find Existing Hook
    const {
        searchTerm,
        setSearchTerm,
        searchResults,
        searching,
        searchError,
        attachingNodeKey,
        handleAttachExisting,
        noResults,
    } = useFindExisting({
        enableSelectionWithoutAnchor: insertMode,
        isOpen,
        showFind: Boolean(anchorNodeKey || insertMode),
        selectedTab: activePanelTab,
        findTabValue: ADD_MODAL_TABS.CREATE,
        workspaceKey,
        anchorNodeKey,
        onAttached: insertMode ? onExistingSelected : onExistingAttached,
        onClose,
    });

    // Create Type Form Hook
    const {
        label,
        setLabel,
        labelPlural,
        description,
        setDescription,
        iconKey,
        setIconKey,
        defaultView,
        setDefaultView,
        isSavingType,
        canSaveType,
        labelHelperText,
        pluralHelperText,
        handleLabelPluralChange,
        handleCreateType,
    } = useCreateTypeForm({
        isOpen,
        typeDefs,
        userKey,
        workspaceKey,
        anchorNodeKey,
        onTypeSelect,
        onClose,
    });

    // Dictionary Hook
    const {
        query: dictionaryQuery,
        setQuery: setDictionaryQuery,
        loading: dictionaryLoading,
        error: dictionaryError,
        terms: dictionaryTerms,
        selectedTerm: dictionarySelectedTerm,
        selectedVariant: dictionarySelectedVariant,
        isVariantStep: dictionaryIsVariantStep,
        isVariantPreviewStep: dictionaryIsVariantPreviewStep,
        isProvisioning: dictionaryIsProvisioning,
        provisioningVariantKey,
        selectedFieldKeys: dictionarySelectedFieldKeys,
        workspaceKey: dictionaryWorkspaceKey,
        handleSelectTerm,
        handleSelectVariant,
        handleBackToTerms,
        handleBackToVariants,
        handleToggleSelectedField,
        handleUseSelectedVariant,
    } = useDictionarySection({
        isOpen,
        selectedTab: activePanelTab,
        dictionaryTabValue: ADD_MODAL_TABS.DICTIONARY,
        workspaceKey,
        availableTypes,
        onTypeSelect,
        onClose,
    });

    // ----- RENDER ----- //

    const moreTabs = [
        {
            key: ADD_MODAL_TABS.DICTIONARY,
            label: 'Databases',
            onClick: () => setMorePanelTab(ADD_MODAL_TABS.DICTIONARY),
        },
        {
            key: ADD_MODAL_TABS.TYPE,
            label: 'Create Database',
            onClick: () => setMorePanelTab(ADD_MODAL_TABS.TYPE),
        },
    ];

    const handleOpenMorePanel = () => {
        if (insertMode) return;
        setMorePanelTab(ADD_MODAL_TABS.DICTIONARY);
        setIsMorePanelOpen(true);
    };

    const handleMorePanelBack = () => {
        if (morePanelTab === ADD_MODAL_TABS.DICTIONARY) {
            if (dictionaryIsVariantPreviewStep) {
                handleBackToVariants();
                return;
            }

            if (dictionaryIsVariantStep) {
                handleBackToTerms();
                return;
            }
        }

        setIsMorePanelOpen(false);
    };

    return (
        <Modal
            iconKey={insertMode ? 'article' : (node ? 'link' : 'wand_shine')}
            title={insertMode ? 'Insert Node' : (node ? `Add to '${node.title}'` : 'Add')}
            isOpen={isOpen}
            onClose={onClose}
            width={isMobile ? '100%' : 500}
            height="80%"
        >
            <ModalForm>

                {/* MAIN PANEL */}
                {!isMorePanelOpen && (
                    <CreateNewSection
                        availableTypes={availableTypes}
                        enableSelectionWithoutAnchor={insertMode}
                        onTypeSelect={onTypeSelect}
                        onMoreClick={handleOpenMorePanel}
                        onClose={onClose}
                        showMoreCard={!insertMode}
                        showInjectedAddTypes={showInjectedAddTypes}
                        showSearchInput={Boolean(anchorNodeKey || insertMode)}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchError={searchError}
                        searching={searching}
                        noResults={noResults}
                        searchResults={searchResults}
                        availableTypeMap={availableTypeMap}
                        anchorNodeKey={anchorNodeKey}
                        attachingNodeKey={attachingNodeKey}
                        onAttachExisting={handleAttachExisting}
                    />
                )}

                {/* MORE PANEL */}
                {!insertMode && isMorePanelOpen && (
                    <div className="add-modal-more-panel">
                        <div className="add-modal-more-actions">
                            <Button
                                label="Back"
                                iconKey="arrow_back"
                                backgroundColor="transparent"
                                hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                                onClick={handleMorePanelBack}
                            />
                        </div>

                        <div className="add-modal-actions">
                            <Tabs
                                tabs={moreTabs}
                                selectedKey={morePanelTab}
                            />
                        </div>

                        {morePanelTab === ADD_MODAL_TABS.DICTIONARY && (
                            <DictionarySection
                                query={dictionaryQuery}
                                setQuery={setDictionaryQuery}
                                loading={dictionaryLoading}
                                error={dictionaryError}
                                terms={dictionaryTerms}
                                selectedTerm={dictionarySelectedTerm}
                                selectedVariant={dictionarySelectedVariant}
                                isVariantStep={dictionaryIsVariantStep}
                                isVariantPreviewStep={dictionaryIsVariantPreviewStep}
                                isProvisioning={dictionaryIsProvisioning}
                                provisioningVariantKey={provisioningVariantKey}
                                selectedFieldKeys={dictionarySelectedFieldKeys}
                                workspaceKey={dictionaryWorkspaceKey}
                                onSelectTerm={handleSelectTerm}
                                onSelectVariant={handleSelectVariant}
                                onToggleSelectedField={handleToggleSelectedField}
                                onBackToTerms={handleBackToTerms}
                                onBackToVariants={handleBackToVariants}
                                onUseSelectedVariant={handleUseSelectedVariant}
                            />
                        )}

                        {morePanelTab === ADD_MODAL_TABS.TYPE && (
                            <CreateTypeSection
                                iconKey={iconKey}
                                setIconKey={setIconKey}
                                isSavingType={isSavingType}
                                canSaveType={canSaveType}
                                label={label}
                                setLabel={setLabel}
                                labelHelperText={labelHelperText}
                                onCreateType={handleCreateType}
                                labelPlural={labelPlural}
                                onLabelPluralChange={handleLabelPluralChange}
                                pluralHelperText={pluralHelperText}
                                description={description}
                                setDescription={setDescription}
                                defaultView={defaultView}
                                setDefaultView={setDefaultView}
                            />
                        )}
                    </div>
                )}

            </ModalForm>

            {!insertMode && isMorePanelOpen && morePanelTab === ADD_MODAL_TABS.DICTIONARY && dictionaryIsVariantPreviewStep && (
                <ModalButtons>
                    <Button
                        iconKey="check"
                        enabled={!dictionaryIsProvisioning}
                        label={dictionaryIsProvisioning ? 'Provisioning...' : 'Continue'}
                        onClick={handleUseSelectedVariant}
                    />
                </ModalButtons>
            )}

        </Modal>
    );
});

AddModal.displayName = 'AddModal';

export default AddModal;
